// Plan v7 / Day 24 F6 — JS port of `config/i18n_routes.php` (PHP).
//
// Hub-spoke URL segment mapping for 5 locales (TR + EN/AR/ES/RU).
// Used by `localizeUrl()` to translate path segments when switching locales.
//
// IMPORTANT: keep this file SYNCED with `config/i18n_routes.php`. Day 26
// hreflang generation depends on identical mappings on both sides.
//
// AR segments are ASCII transliteration (musalsal, shakhs, akhbar...) per
// Plan v7 §3.5 — Cloudflare cache normalization + social share + Google
// preference for romanized URLs.

import type { AppLocale } from './locale-config';

export type SegmentMap = Record<string, string>;

/**
 * Forward map: Turkish segment -> target-locale segment.
 * Each target locale has its full vocabulary.
 */
export const TR_TO_TARGET: Record<Exclude<AppLocale, 'tr'>, SegmentMap> = {
  en: {
    film: 'movie',
    dizi: 'tv-show',
    oyuncu: 'person',
    kisi: 'person',
    filmler: 'movies',
    diziler: 'tv-shows',
    oyuncular: 'actors',
    tur: 'genre',
    turler: 'genres',
    haberler: 'news',
    haber: 'news',
    ara: 'search',
    kullanici: 'user',
    sayfalar: 'pages',
    sayfa: 'page',
    platform: 'platform',
    platformlar: 'platforms',
    yil: 'year',
    izle: 'watch',
    sezon: 'season',
    bolum: 'episode',
    'anahtar-kelime': 'keyword',
    ulkeler: 'countries',
    'ne-izlesem': 'what-to-watch',
    'vizyon-takvimi': 'release-calendar',
    // Kategori vitrinleri (Kore/Hint/Anime) — 'anime' evrensel terim, tüm
    // dillerde aynı kalır (çevrilirse arama hacmi kaybedilir).
    'kore-dizileri': 'korean-series',
    'hint-dizileri': 'indian-series',
    anime: 'anime',
  },
  ar: {
    film: 'film',
    dizi: 'musalsal',
    oyuncu: 'momathel',
    kisi: 'shakhs',
    filmler: 'aflam',
    diziler: 'musalsalat',
    oyuncular: 'momathelin',
    tur: 'naw',
    turler: 'anwa',
    haberler: 'akhbar',
    haber: 'khabar',
    ara: 'bahth',
    kullanici: 'mostakhdim',
    sayfalar: 'safahat',
    sayfa: 'safha',
    platform: 'mansa',
    platformlar: 'manassat',
    yil: 'sana',
    izle: 'mushahada',
    sezon: 'mawsim',
    bolum: 'halqa',
    'anahtar-kelime': 'kalima-muftah',
    ulkeler: 'duwal',
    'ne-izlesem': 'matha-oshahid',
    'vizyon-takvimi': 'taqwim-isdar',
    'kore-dizileri': 'musalsalat-kuriya',
    'hint-dizileri': 'musalsalat-hindiya',
    anime: 'anime',
  },
  es: {
    film: 'pelicula',
    dizi: 'serie',
    oyuncu: 'actor',
    kisi: 'persona',
    filmler: 'peliculas',
    diziler: 'series',
    oyuncular: 'actores',
    tur: 'genero',
    turler: 'generos',
    haberler: 'noticias',
    haber: 'noticia',
    ara: 'buscar',
    kullanici: 'usuario',
    sayfalar: 'paginas',
    sayfa: 'pagina',
    platform: 'plataforma',
    platformlar: 'plataformas',
    yil: 'ano',
    izle: 'ver',
    sezon: 'temporada',
    bolum: 'episodio',
    'anahtar-kelime': 'palabra-clave',
    ulkeler: 'paises',
    'ne-izlesem': 'que-ver',
    'vizyon-takvimi': 'calendario-estrenos',
    'kore-dizileri': 'series-coreanas',
    'hint-dizileri': 'series-indias',
    anime: 'anime',
  },
  ru: {
    film: 'film',
    dizi: 'serial',
    oyuncu: 'akter',
    kisi: 'persona',
    filmler: 'filmy',
    diziler: 'serialy',
    oyuncular: 'aktery',
    tur: 'zhanr',
    turler: 'zhanry',
    haberler: 'novosti',
    haber: 'novost',
    ara: 'poisk',
    kullanici: 'polzovatel',
    sayfalar: 'stranitsy',
    sayfa: 'stranitsa',
    platform: 'platforma',
    platformlar: 'platformy',
    yil: 'god',
    izle: 'smotret',
    sezon: 'sezon',
    bolum: 'epizod',
    'anahtar-kelime': 'klyuchevoe-slovo',
    ulkeler: 'strany',
    'ne-izlesem': 'chto-posmotret',
    'vizyon-takvimi': 'kalendar-premier',
    'kore-dizileri': 'korejskie-serialy',
    'hint-dizileri': 'indijskie-serialy',
    anime: 'anime',
  },
};

/** Memoized reverse maps (target segment -> Turkish segment). */
const REVERSE_CACHE: Partial<Record<Exclude<AppLocale, 'tr'>, SegmentMap>> = {};

export function reverseMap(locale: Exclude<AppLocale, 'tr'>): SegmentMap {
  if (!REVERSE_CACHE[locale]) {
    const forward = TR_TO_TARGET[locale];
    const reverse: SegmentMap = {};
    for (const [tr, target] of Object.entries(forward)) {
      // First-write-wins: if multiple TR segments map to the same target
      // (e.g. "haberler" and "haber" both -> "news" in EN), prefer the more
      // specific/listed-first one.
      if (!(target in reverse)) {
        reverse[target] = tr;
      }
    }
    REVERSE_CACHE[locale] = reverse;
  }
  return REVERSE_CACHE[locale]!;
}
